import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/EditOutlined'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import BuscaPessoa, { PessoaAchada } from '../componentes/BuscaPessoa'
import { plural } from './comum'
import { baixarArquivo, linhasDeItens, LinhaItem } from './csv'
import './gestao.scss'

interface RegraResumo { id: number; nome: string; tabela: string; coluna: string; itens: number; pessoas: number; dashboards: number; semValorVeTudo: boolean }
interface Item { id: number; pessoaId: number; nome: string; email: string; empresa?: string | null; atributo: string; valor: string; dashboardId?: number | null; dashboard?: string | null; ambienteId?: number | null; ambiente?: string | null }
interface Uso { dashboardId: number; dashboard: string; ambienteId: number; ambiente: string }
interface SemValor { id: number; nome: string; email: string; empresa?: string | null; dashboards: number }
interface Detalhe { id: number; nome: string; tabela: string; coluna: string; itens: Item[]; usadaEm: Uso[]; semValor: SemValor[]; semValorTotal: number; semValorVeTudo: boolean }
interface Previa { lidas: number; prontas: number; jaExistiam: number; problemas: { linha: number; email: string; valor: string; motivo: string }[]; gravados: number; removidos: number }

const valeEmTodos = (i: Item) => !(i.dashboardId && i.ambienteId)

/** Gestão › Regras: o recorte de dado por pessoa. Cada item dá a uma pessoa um valor da coluna. */
export default function Regras() {
  const { id } = useParams()
  const atual = Number(id) || null
  const navigate = useNavigate()
  const { notify } = useNotification()

  const [lista, setLista] = useState<RegraResumo[] | null>(null)
  const [busca, setBusca] = useState('')
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [def, setDef] = useState({ nome: '', tabela: '', coluna: '' })
  const [buscaItem, setBuscaItem] = useState('')
  const [valores, setValores] = useState<Record<number, { atributo: string; valor: string }>>({})
  const [nova, setNova] = useState(false)
  const [novaDef, setNovaDef] = useState({ nome: '', tabela: '', coluna: '' })
  const [novoItem, setNovoItem] = useState<{ pessoa: PessoaAchada | null; atributo: string; valor: string; uso: string } | null>(null)
  const [importar, setImportar] = useState(false)
  const [excluir, setExcluir] = useState(false)
  const [editarItem, setEditarItem] = useState<{ item: Item; atributo: string; valor: string; uso: string } | null>(null)
  const [confirmarVeTudo, setConfirmarVeTudo] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erroJanela, setErroJanela] = useState<string | null>(null)

  const carregarLista = useCallback(async () => {
    const r = await services.api.novoPortalRegras()
    setLista(r.fail ? [] : r.data || [])
  }, [])

  const carregarDetalhe = useCallback(async () => {
    setErro(null)
    if (!atual) { setDetalhe(null); return }
    const r = await services.api.novoPortalRegra(atual)
    if (r.fail) { setDetalhe(null); setErro(r.error || 'Não foi possível abrir a regra.'); return }
    setDetalhe(r.data)
    setDef({ nome: r.data.nome, tabela: r.data.tabela, coluna: r.data.coluna })
  }, [atual])

  useEffect(() => { carregarLista() }, [carregarLista])
  useEffect(() => { setBuscaItem(''); setValores({}); carregarDetalhe() }, [carregarDetalhe])
  useEffect(() => {
    if (!atual && lista?.length && window.innerWidth > 768) navigate(`/app/gestao/regras/${lista[0].id}`, { replace: true })
  }, [atual, lista, navigate])

  const recarregar = () => { carregarDetalhe(); carregarLista() }

  const q = busca.trim().toLowerCase()
  const visiveis = (lista || []).filter(r => !q || `${r.nome} ${r.tabela} ${r.coluna}`.toLowerCase().includes(q))
  const qi = buscaItem.trim().toLowerCase()
  const itens = useMemo(() => (detalhe?.itens || []).filter(i => !qi || `${i.nome} ${i.email} ${i.empresa} ${i.atributo} ${i.valor} ${i.dashboard} ${i.ambiente}`.toLowerCase().includes(qi)), [detalhe, qi])
  const pessoasNaRegra = new Set((detalhe?.itens || []).map(i => i.pessoaId)).size
  const mudouDef = !!detalhe && (def.nome !== detalhe.nome || def.tabela !== detalhe.tabela || def.coluna !== detalhe.coluna)

  const criarItem = async (pessoaId: number, atributo: string, valor: string, uso?: Uso) => {
    const r = await services.api.createNewDashboardRuleItem({
      attribute: atributo.trim(), value: valor.trim(), userId: pessoaId, dashboardRuleId: detalhe!.id,
      dashboardId: uso?.dashboardId ?? null, environmentId: uso?.ambienteId ?? null
    })
    return r.fail ? (r.error || 'Não foi possível adicionar o item.') : null
  }

  const salvarDef = async () => {
    if (!detalhe) return
    const r = await services.api.updateDashboardRule({ Id: detalhe.id, Name: def.nome.trim(), Table: def.tabela.trim(), Column: def.coluna.trim() })
    if (r.fail) { notify(r.error || 'Não foi possível salvar.', { variant: 'error' }); return }
    notify('Regra salva. Vale na hora para todos os dashboards que a usam.', { variant: 'success' })
    recarregar()
  }

  const definirSemValor = async (veTudo: boolean) => {
    if (!detalhe) return
    setSalvando(true)
    const r = await services.api.novoPortalSemValor(detalhe.id, veTudo)
    setSalvando(false)
    setConfirmarVeTudo(false)
    if (r.fail) { notify(r.error || 'Não foi possível salvar.', { variant: 'error' }); return }
    notify(veTudo ? 'Quem não tem item nesta regra agora vê tudo.' : 'Quem não tem item nesta regra agora não vê nada.', { variant: 'success' })
    recarregar()
  }

  const criarRegra = async () => {
    setSalvando(true); setErroJanela(null)
    const r = await services.api.createNewDashboardRule({ name: novaDef.nome.trim(), table: novaDef.tabela.trim(), column: novaDef.coluna.trim() })
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível criar a regra.'); return }
    setNova(false)
    notify('Regra criada. Agora adicione os itens.', { variant: 'success' })
    await carregarLista()
    if (r.data?.id) navigate(`/app/gestao/regras/${r.data.id}`)
  }

  const confirmarExcluir = async () => {
    if (!detalhe) return
    setSalvando(true)
    const r = await services.api.deleteDashboardRule(detalhe.id)
    setSalvando(false)
    if (r.fail) { notify(r.error || 'Não foi possível excluir.', { variant: 'error' }); return }
    setExcluir(false)
    notify(`Regra ${detalhe.nome} excluída. Os dashboards que a usavam ficaram sem regra.`, { variant: 'success' })
    await carregarLista()
    navigate('/app/gestao/regras')
  }

  const tirarItem = async (i: Item) => {
    const r = await services.api.deleteDashboardFilterRule(i.id)
    if (r.fail) { notify(r.error || 'Não foi possível remover o item.', { variant: 'error' }); return }
    notify(`${i.nome} perdeu o item ${[i.atributo, i.valor].filter(Boolean).join(' · ')}.`, { variant: 'success' })
    recarregar()
  }

  const salvarNovoItem = async () => {
    if (!novoItem?.pessoa || !detalhe) return
    setSalvando(true); setErroJanela(null)
    const uso = novoItem.uso ? detalhe.usadaEm.find(u => `${u.dashboardId}:${u.ambienteId}` === novoItem.uso) : undefined
    const problema = await criarItem(novoItem.pessoa.id, novoItem.atributo, novoItem.valor, uso)
    setSalvando(false)
    if (problema) { setErroJanela(problema); return }
    setNovoItem(null)
    notify('Item adicionado.', { variant: 'success' })
    recarregar()
  }

  const abrirEdicao = (i: Item) => {
    setErroJanela(null)
    setEditarItem({ item: i, atributo: i.atributo || '', valor: i.valor, uso: valeEmTodos(i) ? '' : `${i.dashboardId}:${i.ambienteId}` })
  }

  // Onde o item pode valer: os dashboards que usam a regra e, se ele já valia para um par que a regra
  // não usa mais, esse par também (senão a edição o transformaria em "todos" sem a pessoa perceber).
  const opcoesDeUso = (i: Item): { valor: string; texto: string }[] => {
    const lista = (detalhe?.usadaEm || []).map(u => ({ valor: `${u.dashboardId}:${u.ambienteId}`, texto: `Só ${u.dashboard} em ${u.ambiente}` }))
    const atual = valeEmTodos(i) ? null : `${i.dashboardId}:${i.ambienteId}`
    if (atual && !lista.some(o => o.valor === atual)) lista.push({ valor: atual, texto: `Só ${i.dashboard} em ${i.ambiente} (não usa mais a regra)` })
    return lista
  }

  const salvarEdicao = async () => {
    if (!editarItem || !detalhe) return
    const { item, atributo, valor, uso } = editarItem
    const par = uso ? uso.split(':').map(Number) : null
    setSalvando(true); setErroJanela(null)
    const r = await services.api.updateDashboardRuleItem({
      id: item.id, attribute: atributo.trim(), value: valor.trim(),
      dashboardId: par ? par[0] : null, environmentId: par ? par[1] : null
    })
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível salvar o item.'); return }
    setEditarItem(null)
    notify('Item atualizado.', { variant: 'success' })
    recarregar()
  }

  const darValor = async (p: SemValor) => {
    const { atributo = '', valor = '' } = valores[p.id] || {}
    if (!valor.trim()) return
    const problema = await criarItem(p.id, atributo, valor)
    if (problema) { notify(problema, { variant: 'error' }); return }
    notify(`${p.nome} agora tem o item ${[atributo.trim(), valor.trim()].filter(Boolean).join(' · ')}.`, { variant: 'success' })
    setValores(v => ({ ...v, [p.id]: { atributo: '', valor: '' } }))
    recarregar()
  }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Regras</h1>
        <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => { setNovaDef({ nome: '', tabela: '', coluna: '' }); setErroJanela(null); setNova(true) }}><AddIcon />Nova regra</button></div>
      </div>
      <p className="ng-explic">Uma regra recorta uma coluna do modelo de acordo com quem abre o dashboard. Cada item dá a uma pessoa um valor dessa coluna.</p>

      <div className={`ng-ambientes${atual ? ' com-detalhe' : ''}`}>
        <aside className="ng-cartao ng-amb-lista" aria-label="Regras">
          <label className="ng-busca"><SearchIcon fontSize="small" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar regra ou coluna" aria-label="Buscar regra" />
          </label>
          <div className="ng-amb-itens">
            {!lista && <p className="ng-explic">Carregando…</p>}
            {lista && visiveis.length === 0 && <p className="ng-explic">Nenhuma regra.</p>}
            {visiveis.map(r => (
              <button key={r.id} type="button" className="ng-amb-item" aria-current={r.id === atual ? 'page' : undefined} onClick={() => navigate(`/app/gestao/regras/${r.id}`)}>
                <span className="ng-amb-ini">{r.nome.charAt(0)}</span>
                <span className="ng-amb-txt"><b>{r.nome}</b><small>{r.tabela}[{r.coluna}] · {plural(r.pessoas, 'pessoa', 'pessoas')}{r.semValorVeTudo ? ' · sem item: vê tudo' : ''}</small></span>
              </button>
            ))}
          </div>
        </aside>

        <section className="ng-amb-detalhe">
          {erro && <div className="ng-cartao"><p className="ng-explic">{erro}</p></div>}
          {atual && !detalhe && !erro && <div className="ng-cartao"><p className="ng-explic">Carregando…</p></div>}
          {detalhe && (
            <>
              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <button type="button" className="ng-btn ng-voltar" onClick={() => navigate('/app/gestao/regras')}>‹ Regras</button>
                  <h2>{detalhe.nome}</h2>
                  <div className="ng-acoes"><button type="button" className="ng-btn perigo" onClick={() => setExcluir(true)}>Excluir</button></div>
                </div>
                <p className="ng-explic">{plural(pessoasNaRegra, 'pessoa', 'pessoas')} · {plural(detalhe.itens.length, 'item', 'itens')} · usada em {plural(new Set(detalhe.usadaEm.map(u => u.dashboardId)).size, 'dashboard', 'dashboards')}</p>
                <div className="ng-campos" style={{ marginTop: 12 }}>
                  <label className="ng-campo largo"><span>Nome</span><input value={def.nome} maxLength={100} onChange={e => setDef({ ...def, nome: e.target.value })} /></label>
                  <label className="ng-campo"><span>Tabela</span><input value={def.tabela} maxLength={100} onChange={e => setDef({ ...def, tabela: e.target.value })} /></label>
                  <label className="ng-campo"><span>Coluna</span><input value={def.coluna} maxLength={100} onChange={e => setDef({ ...def, coluna: e.target.value })} /></label>
                </div>
                <p className="ng-frase" style={{ marginTop: 12 }}>Cada pessoa vê só as linhas em que <code>{def.tabela || '…'}[{def.coluna || '…'}]</code> é um dos valores dela. Quem administra o BI vê tudo.</p>
                {mudouDef && (
                  <div className="ng-rodape-cartao" style={{ marginTop: 0 }}>
                    <button type="button" className="ng-btn prim" disabled={!def.nome.trim() || !def.tabela.trim() || !def.coluna.trim()} onClick={salvarDef}>Salvar alterações</button>
                    <button type="button" className="ng-btn" onClick={() => setDef({ nome: detalhe.nome, tabela: detalhe.tabela, coluna: detalhe.coluna })}>Desfazer</button>
                  </div>
                )}
              </div>

              <div className="ng-cartao">
                <h2>Quem não tem item nesta regra</h2>
                <p className="ng-explic">Vale para quem tem acesso a um dashboard que usa a regra e nenhum item nela. Quem tem item vê só o dele, nas duas opções. Os filtros do ambiente valem sempre.</p>
                <label className="ng-campo" style={{ maxWidth: 320, marginTop: 8 }}>
                  <span>Em caso de não possuir item</span>
                  <select className="ng-select" value={detalhe.semValorVeTudo ? 'tudo' : 'nada'} disabled={salvando} aria-label="O que acontece com quem não tem item"
                          onChange={e => { if (e.target.value === 'tudo') setConfirmarVeTudo(true); else definirSemValor(false) }}>
                    <option value="nada">Não ver nada</option>
                    <option value="tudo">Ver tudo</option>
                  </select>
                </label>
                <p className="ng-frase" style={{ marginTop: 8 }}>
                  {detalhe.semValorVeTudo
                    ? 'Quem não tem item abre o dashboard sem o recorte da regra: vê todos os dados.'
                    : 'Quem não tem item abre o dashboard vazio, por segurança. É o padrão.'}
                </p>
              </div>

              {detalhe.semValorTotal > 0 && (
                <div className="ng-cartao ng-alerta">
                  <h2>{detalhe.semValorVeTudo
                    ? `${plural(detalhe.semValorTotal, 'pessoa vê', 'pessoas veem')} tudo, sem item nesta regra`
                    : `${plural(detalhe.semValorTotal, 'pessoa abre', 'pessoas abrem')} dashboards vazios`}</h2>
                  <p className="ng-explic">Têm acesso a dashboards que usam esta regra, mas não têm valor nela.{detalhe.semValorVeTudo ? ' Como a regra libera quem não tem item, elas veem todos os dados; dar um item a uma pessoa passa a limitá-la ao valor dele.' : ''} Administradores do BI aparecem aqui também, mas para eles não faz diferença: veem tudo.{detalhe.semValorTotal > detalhe.semValor.length ? ` Mostrando ${detalhe.semValor.length}.` : ''}</p>
                  <div className="ng-lista" style={{ maxHeight: 320 }}>
                    {detalhe.semValor.map(p => (
                      <div key={p.id} className="ng-linha">
                        <span className="ng-amb-ini">{p.nome.charAt(0)}</span>
                        <span className="ng-amb-txt"><b>{p.nome}</b><small>{[p.email, p.empresa].filter(Boolean).join(' · ')} · {plural(p.dashboards, 'dashboard vazio', 'dashboards vazios')}</small></span>
                        <input className="ng-select" style={{ width: 150 }} value={valores[p.id]?.atributo || ''} placeholder="Atributo" aria-label={`Atributo de ${p.nome}`}
                               onChange={e => setValores(v => ({ ...v, [p.id]: { atributo: e.target.value, valor: v[p.id]?.valor || '' } }))} onKeyDown={e => { if (e.key === 'Enter') darValor(p) }} />
                        <input className="ng-select" style={{ width: 150 }} value={valores[p.id]?.valor || ''} placeholder="Valor" aria-label={`Valor de ${p.nome}`}
                               onChange={e => setValores(v => ({ ...v, [p.id]: { atributo: v[p.id]?.atributo || '', valor: e.target.value } }))} onKeyDown={e => { if (e.key === 'Enter') darValor(p) }} />
                        <button type="button" className="ng-btn" disabled={!(valores[p.id]?.valor || '').trim()} onClick={() => darValor(p)}>Adicionar</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <h2>Itens da regra</h2>
                  <div className="ng-acoes">
                    <button type="button" className="ng-btn" onClick={() => setImportar(true)}><UploadIcon />Importar CSV</button>
                    <button type="button" className="ng-btn prim" onClick={() => { setErroJanela(null); setNovoItem({ pessoa: null, atributo: '', valor: '', uso: '' }) }}><AddIcon />Adicionar item</button>
                  </div>
                </div>
                <p className="ng-explic">Cada linha é um item. A mesma pessoa pode ter vários: quem tem Sul e Sudeste vê as duas. “Vale para” limita o item a um dashboard num ambiente.</p>
                {detalhe.itens.length > 8 && (
                  <div className="ng-filtros" style={{ marginBottom: 8 }}>
                    <label className="ng-busca"><SearchIcon fontSize="small" />
                      <input value={buscaItem} onChange={e => setBuscaItem(e.target.value)} placeholder="Buscar por pessoa, atributo ou valor" aria-label="Buscar item" />
                    </label>
                  </div>
                )}
                <div className="ng-tabela">
                  <table>
                    <thead><tr><th>Pessoa</th><th>Atributo</th><th>Valor</th><th>Vale para</th><th /></tr></thead>
                    <tbody>
                      {itens.length === 0 && <tr><td colSpan={5} className="vazio">{detalhe.itens.length ? 'Nenhum item com essa busca.' : 'Nenhum item ainda: quem abre um dashboard com esta regra vê vazio.'}</td></tr>}
                      {itens.slice(0, 500).map(i => (
                        <tr key={i.id}>
                          <td><b>{i.nome}</b><small style={{ display: 'block', color: '#8b92a1' }}>{[i.email, i.empresa].filter(Boolean).join(' · ')}</small></td>
                          <td>{i.atributo ? <span className="ng-tag">{i.atributo}</span> : <span style={{ color: '#8b92a1' }}>—</span>}</td>
                          <td><span className="ng-tag">{i.valor}</span></td>
                          <td>{valeEmTodos(i) ? 'Todos os dashboards' : `${i.dashboard} em ${i.ambiente}`}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button type="button" className="ng-ico" onClick={() => abrirEdicao(i)} aria-label={`Editar o item ${i.atributo} ${i.valor} de ${i.nome}`} title="Editar"><EditIcon fontSize="small" /></button>
                            <button type="button" className="ng-ico perigo" onClick={() => tirarItem(i)} aria-label={`Remover o item ${i.atributo} ${i.valor} de ${i.nome}`} title="Remover"><CloseIcon fontSize="small" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {itens.length > 500 && <p className="ng-explic" style={{ textAlign: 'center', padding: 8 }}>Mostrando 500 de {itens.length}. Use a busca.</p>}
                </div>
              </div>

              <div className="ng-cartao">
                <h2>Usada em</h2>
                <p className="ng-explic">Dashboards que escolheram esta regra em algum ambiente.</p>
                {detalhe.usadaEm.length === 0
                  ? <p className="ng-explic">Nenhum dashboard usa esta regra ainda. Escolha a regra na página do dashboard no ambiente.</p>
                  : <div className="ng-chips">{detalhe.usadaEm.map(u => (
                    <Link key={`${u.dashboardId}:${u.ambienteId}`} className="ng-chip" to={`/app/gestao/dashboards/${u.dashboardId}/ambiente/${u.ambienteId}`}><b>{u.dashboard}</b><small>{u.ambiente}</small></Link>
                  ))}</div>}
              </div>
            </>
          )}
        </section>
      </div>

      <Janela aberto={nova} titulo="Nova regra" sub="Depois você adiciona os itens e escolhe a regra nos dashboards." botao="Criar regra" salvando={salvando} erro={erroJanela}
              desabilitado={!novaDef.nome.trim() || !novaDef.tabela.trim() || !novaDef.coluna.trim()} aoConfirmar={criarRegra} aoFechar={() => setNova(false)}>
        <label className="nv-campo"><span>Nome</span><input value={novaDef.nome} maxLength={100} onChange={e => setNovaDef({ ...novaDef, nome: e.target.value })} placeholder="Ex.: Visão regional" /></label>
        <div className="nv-campos">
          <label className="nv-campo"><span>Tabela do modelo</span><input value={novaDef.tabela} maxLength={100} onChange={e => setNovaDef({ ...novaDef, tabela: e.target.value })} placeholder="Ex.: dCTT" /></label>
          <label className="nv-campo"><span>Coluna</span><input value={novaDef.coluna} maxLength={100} onChange={e => setNovaDef({ ...novaDef, coluna: e.target.value })} placeholder="Ex.: REGIONAL" /></label>
        </div>
        <small style={{ color: '#8b92a1' }}>Use o nome exato da tabela e da coluna no Power BI: é ali que o filtro é aplicado.</small>
      </Janela>

      {detalhe && (
        <Janela aberto={!!novoItem} titulo="Adicionar item" sub={`Regra ${detalhe.nome} · ${detalhe.tabela}[${detalhe.coluna}]`} botao="Adicionar item" salvando={salvando} erro={erroJanela}
                desabilitado={!novoItem?.pessoa || !novoItem.valor.trim()} aoConfirmar={salvarNovoItem} aoFechar={() => setNovoItem(null)}>
          {novoItem && (
            <>
              <div className="nv-campo"><span>Pessoa</span><BuscaPessoa valor={novoItem.pessoa} aoEscolher={p => setNovoItem({ ...novoItem, pessoa: p })} /></div>
              <div className="nv-campos">
                <label className="nv-campo"><span>Atributo</span><input value={novoItem.atributo} maxLength={500} onChange={e => setNovoItem({ ...novoItem, atributo: e.target.value })} placeholder="Ex.: CLIENTE_" /></label>
                <label className="nv-campo"><span>Valor</span><input value={novoItem.valor} maxLength={500} onChange={e => setNovoItem({ ...novoItem, valor: e.target.value })} placeholder="Ex.: AMIL" /></label>
              </div>
              <label className="nv-campo"><span>Vale para</span>
                <select value={novoItem.uso} onChange={e => setNovoItem({ ...novoItem, uso: e.target.value })}>
                  <option value="">Todos os dashboards que usam a regra</option>
                  {detalhe.usadaEm.map(u => <option key={`${u.dashboardId}:${u.ambienteId}`} value={`${u.dashboardId}:${u.ambienteId}`}>Só {u.dashboard} em {u.ambiente}</option>)}
                </select>
              </label>
            </>
          )}
        </Janela>
      )}

      {detalhe && (
        <Janela aberto={confirmarVeTudo} titulo="Liberar quem não tem item" sub={`Regra ${detalhe.nome} · ${detalhe.tabela}[${detalhe.coluna}]`} botao="Liberar" perigo salvando={salvando}
                aoConfirmar={() => definirSemValor(true)} aoFechar={() => setConfirmarVeTudo(false)}>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            {detalhe.semValorTotal > 0
              ? <>Hoje <b>{plural(detalhe.semValorTotal, 'pessoa tem', 'pessoas têm')}</b> acesso a dashboards desta regra e nenhum item nela.</>
              : <>Hoje ninguém está nessa situação, mas qualquer pessoa que ganhar acesso a um dashboard desta regra sem item vai nela.</>}
            {' '}Com “Ver tudo”, essas pessoas passam a ver <b>todos os dados</b> desses dashboards, sem o recorte da regra. Quem tem item continua vendo só o dele. Os filtros do ambiente continuam valendo.
          </p>
        </Janela>
      )}

      {detalhe && <ImportarItens aberto={importar} regra={detalhe} aoFechar={() => setImportar(false)} aoImportar={recarregar} />}

      {detalhe && (
        <Janela aberto={!!editarItem} titulo="Editar item" sub={editarItem ? `${editarItem.item.nome} · regra ${detalhe.nome} · ${detalhe.tabela}[${detalhe.coluna}]` : ''} botao="Salvar" salvando={salvando} erro={erroJanela}
                desabilitado={!editarItem?.valor.trim()} aoConfirmar={salvarEdicao} aoFechar={() => setEditarItem(null)}>
          {editarItem && (
            <>
              <div className="nv-campos">
                <label className="nv-campo"><span>Atributo</span><input value={editarItem.atributo} maxLength={500} onChange={e => setEditarItem({ ...editarItem, atributo: e.target.value })} placeholder="Ex.: CLIENTE_" /></label>
                <label className="nv-campo"><span>Valor</span><input value={editarItem.valor} maxLength={500} onChange={e => setEditarItem({ ...editarItem, valor: e.target.value })} placeholder="Ex.: AMIL" /></label>
              </div>
              <label className="nv-campo"><span>Vale para</span>
                <select value={editarItem.uso} onChange={e => setEditarItem({ ...editarItem, uso: e.target.value })}>
                  <option value="">Todos os dashboards que usam a regra</option>
                  {opcoesDeUso(editarItem.item).map(o => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
                </select>
              </label>
              <small style={{ color: '#8b92a1' }}>Para trocar a pessoa, remova o item e adicione de novo para a pessoa certa.</small>
            </>
          )}
        </Janela>
      )}

      <Janela aberto={excluir} titulo="Excluir regra" botao="Excluir de vez" perigo salvando={salvando} aoConfirmar={confirmarExcluir} aoFechar={() => setExcluir(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Excluir <b>{detalhe?.nome}</b> e seus {plural(detalhe?.itens.length || 0, 'item', 'itens')}? Os dashboards que usam a regra ficam sem regra: todos que têm acesso passam a ver o dado inteiro.</p>
      </Janela>
    </div>
  )
}

/** Importa itens de um CSV: primeiro a prévia (nada gravado), depois a confirmação. */
function ImportarItens({ aberto, regra, aoFechar, aoImportar }: { aberto: boolean; regra: Detalhe; aoFechar: () => void; aoImportar: () => void }) {
  const { notify } = useNotification()
  const [linhas, setLinhas] = useState<LinhaItem[] | null>(null)
  const [arquivo, setArquivo] = useState('')
  const [substituir, setSubstituir] = useState(false)
  const [previa, setPrevia] = useState<Previa | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => { if (aberto) { setLinhas(null); setArquivo(''); setSubstituir(false); setPrevia(null); setErro(null) } }, [aberto])

  const corpo = useCallback((gravar: boolean) => ({
    linhas: (linhas || []).map(l => ({ linha: l.linha, email: l.email, atributo: l.atributo || '', valor: l.valor, dashboard: l.dashboard || null, ambiente: l.ambiente || null })),
    substituir, gravar
  }), [linhas, substituir])

  // Prévia de novo sempre que a planilha ou o modo mudam.
  useEffect(() => {
    if (!linhas?.length) { setPrevia(null); return }
    let vale = true
    setOcupado(true); setErro(null)
    services.api.novoPortalImportarItens(regra.id, corpo(false)).then((r: any) => {
      if (!vale) return
      setOcupado(false)
      if (r.fail) { setErro(r.error || 'Não foi possível conferir a planilha.'); setPrevia(null) } else setPrevia(r.data)
    })
    return () => { vale = false }
  }, [linhas, substituir, regra.id, corpo])

  const ler = async (f: File | undefined) => {
    if (!f) return
    setArquivo(f.name)
    if (!/\.csv$/i.test(f.name)) { setErro('Envie um arquivo .csv (no Excel: Salvar como › CSV).'); setLinhas(null); return }
    const lidas = linhasDeItens(await f.text())
    if (lidas.length === 0) { setErro('A planilha está vazia.'); setLinhas(null); return }
    if (lidas.length > 5000) { setErro(`A planilha tem ${lidas.length} linhas; o máximo é 5.000.`); setLinhas(null); return }
    setLinhas(lidas)
  }

  const confirmar = async () => {
    setOcupado(true); setErro(null)
    const r = await services.api.novoPortalImportarItens(regra.id, corpo(true))
    setOcupado(false)
    if (r.fail) { setErro(r.error || 'Não foi possível importar.'); return }
    notify(`${plural(r.data.gravados, 'item importado', 'itens importados')}${r.data.removidos ? `, no lugar de ${r.data.removidos} anteriores` : ''}.`, { variant: 'success' })
    aoFechar()
    aoImportar()
  }

  return (
    <Janela aberto={aberto} titulo="Importar itens" sub={`Regra ${regra.nome} · ${regra.tabela}[${regra.coluna}]`} salvando={ocupado} erro={erro}
            botao={previa?.prontas ? `Importar ${plural(previa.prontas, 'item', 'itens')}` : 'Importar'} desabilitado={!previa?.prontas} aoConfirmar={confirmar} aoFechar={aoFechar}>
      <div className="ng-passo"><span>1</span><div><b>Baixe o modelo</b><small>Colunas: e-mail da pessoa; atributo; valor; dashboard; ambiente. Atributo pode ficar vazio. Dashboard e ambiente (nome ou id) só se o item valer para um dashboard num ambiente.</small></div>
        <button type="button" className="nv-btn" onClick={() => baixarArquivo(`itens-${regra.nome.toLowerCase().replace(/\s+/g, '-')}.csv`, 'email;atributo;valor;dashboard;ambiente\nmaria@empresa.com.br;CLIENTE_;AMIL;;\n')}>Baixar modelo</button>
      </div>
      <div className="ng-passo"><span>2</span><div><b>Envie a planilha preenchida</b><small>.csv, até 5.000 linhas. Nada é gravado antes de você confirmar.</small></div>
        <label className="nv-btn" style={{ cursor: 'pointer' }}>Escolher arquivo<input type="file" accept=".csv,text/csv" hidden onChange={e => { ler(e.target.files?.[0]); e.target.value = '' }} /></label>
      </div>
      {arquivo && <small style={{ color: '#5b6273' }}>Arquivo: {arquivo}{linhas ? ` · ${plural(linhas.length, 'linha', 'linhas')}` : ''}</small>}
      {linhas && ocupado && !previa && <p className="nv-resumo" style={{ margin: 0 }}>Conferindo as linhas…</p>}
      {previa && (
        <>
          <div className="ng-numeros">
            <div><b>{previa.lidas}</b><span>lidas</span></div>
            <div className="ok"><b>{previa.prontas}</b><span>prontas</span></div>
            <div><b>{previa.jaExistiam}</b><span>já existiam</span></div>
            <div className={previa.problemas.length ? 'erro' : ''}><b>{previa.problemas.length}</b><span>com problema</span></div>
          </div>
          {previa.problemas.length > 0 && (
            <div className="ng-tabela" style={{ maxHeight: 220, overflow: 'auto' }}>
              <table>
                <thead><tr><th>Linha</th><th>E-mail</th><th>Atributo</th><th>Valor</th><th>Problema</th></tr></thead>
                <tbody>{previa.problemas.slice(0, 200).map(p => <tr key={p.linha}><td>{p.linha}</td><td>{p.email}</td><td>{(linhas || []).find(l => l.linha === p.linha)?.atributo}</td><td>{p.valor}</td><td style={{ color: '#c4362b', fontWeight: 700 }}>{p.motivo}</td></tr>)}</tbody>
              </table>
            </div>
          )}
          {previa.problemas.length > 0 && <small style={{ color: '#8b92a1' }}>Linhas com problema ficam de fora.</small>}
          <div className="nv-campo"><span>O que fazer com os itens que já existem</span>
            <label className="ng-toggle"><input type="radio" name="imp-modo" checked={!substituir} onChange={() => setSubstituir(false)} />Somar: os itens da planilha entram junto com os atuais</label>
            <label className="ng-toggle"><input type="radio" name="imp-modo" checked={substituir} onChange={() => setSubstituir(true)} />Substituir: a regra fica só com o que está na planilha ({plural(regra.itens.length, 'item atual sai', 'itens atuais saem')})</label>
          </div>
        </>
      )}
    </Janela>
  )
}
